import { Field } from 'contensis-core-api';
import { EntryAsset, TaxonomyNode } from 'contensis-delivery-api';
import { Entry, ICreateTag, Tag } from 'contensis-management-api';
import { Block, LinkBlock } from '@contensis/canvas-html';
import { TagBuilder } from './TagBuilder';
import { EntryDependencyMeta } from '~/models/EntryDependencies';
import { IContensisContext } from '~/models/IContensisContext';
import ContensisRepository from '~/repositories/ContensisRepository';
import { findBlockById } from '~/util/canvas';
import {
  isArray,
  isNullOrUndefined,
  isNumber,
  isString,
  isUndefined,
} from '~/util/typeguards';
import { Logger } from '~/util/logger';

export interface IEntryFields {
  [k: string]: EntryField | EntryField[];
}

type ComposerFields = EntryField[];

type ComponentFields = IEntryFields | EntryField[];

// A composer or component field in an entry will have their own built fields
type InnerFields = ComponentFields | ComposerFields | undefined;

/**
 * Supplying the field configuration from the target content type, the context containing
 * the content models for the parent entry *and the target repository, we can take a given value
 * from the source and build a complete picture of the field specifically for the target entry
 * checking dependencies and setting flag to trigger a two-pass loading status on the entry if
 * any dependents do not exist
 * Using the toJSON method to produce the entry as JSON for loading into the Management API
 */
export class EntryField {
  private currentCanvasBlockId?: string;
  private existingEntry?: Entry;
  requireSecondPass = false;
  firstPassValue?: any;
  finalValue: any;
  fields: InnerFields = undefined;
  /** log any issues with building the field */
  warnings: string[] = [];

  get isRequired() {
    return this.context.field.assert.validation.IsRequired(this.field);
  }
  get isRepeatable() {
    return this.context.field.assert.IsRepeatable(this.field);
  }
  get isComposerChild() {
    return this.parentFields?.some((f) =>
      this.context.field.assert.IsComposer(f)
    );
  }
  get isComponent() {
    return this.context.field.assert.IsComponent(this.field);
  }
  get isComponentChild() {
    return this.parentFields?.some((f) =>
      this.context.field.assert.IsComponent(f)
    );
  }

  get language() {
    return this.entry.sys.language;
  }

  /**
   * a good indicator that the entry field is being created for a
   * default entry in a model migration
   */
  get builtContentType() {
    return this.contensis.models.migrate.contentTypes.find(
      (c) => c.id === this.entry.sys.contentTypeId
    );
  }

  /**
   * Look for the current value in an existing entry
   * Only needed for tidying up the diffs for example
   * some linked content returns `sys.language` in the
   * management api data and some doesn't so we can map
   * it if it's already there in the management data and
   * leave it out if not
   */
  get existingValue() {
    const entry = this.existingEntry;
    if (entry) {
      if (this.parentFields?.length) {
        let val: any = undefined;
        for (let i = 0; i < this.parentFields.length; i++) {
          const parent = this.parentFields[i];
          const prevParent = i > 0 ? this.parentFields[i - 1] : undefined;
          const fieldId = parent.id;

          if (
            this.isCanvasField &&
            this.context.field.assert.IsComponent(parent) &&
            (this.parentFields?.length || 0) < 2 // gtEq 2 means we're in a nested component
          ) {
            val = findBlockById(fieldId, val)?.value;
          } else if (
            prevParent &&
            this.context.field.assert.IsComposer(prevParent) &&
            Array.isArray(val)
          ) {
            // Unwrap-and-continue. Composers are only ever allowed as a
            // top-level content-type field (never nested inside another
            // field), and their stored value is always a flat [] of
            // {type, value} items. If the *previous* iteration walked into
            // one, `val` is that flat array — filter to items whose type
            // matches this ancestor's id and swap in the component `value`
            // shape so the next iteration's `val?.[fieldId]` lookup can
            // proceed against a plain object.
            const items = val
              .filter((v) => v?.type === fieldId)
              .map((v) => v?.value);
            val = this.context.field.assert.IsRepeatable(parent)
              ? items
              : items[0];
          } else
            val =
              typeof val !== 'undefined' ? val?.[fieldId] : entry?.[fieldId];
        }
        // Post-loop projection onto `this.field.id`. Three cases:
        //   1. Unwrap-and-return: val is a composer array AND our immediate
        //      parent is that composer (parentFields === [composer]). The
        //      unwrap-and-continue branch above never fired because there
        //      was no previous ancestor to detect. Filter composer items by
        //      our field id and map to their `value` — this becomes the
        //      final result (no further ancestors to walk).
        //   2. val is any other array (e.g. a repeatable objectArray
        //      resolved by a preceding key lookup) — take field.id off each
        //      item so we produce a parallel array of values.
        //   3. val is a plain object — read field.id from it.
        return Array.isArray(val)
          ? this.context.field.assert.IsComposer(this.parentField || this.field)
            ? val.filter((v) => v.type === this.field.id).map((v) => v.value)
            : val.map((v) => v?.[this.field.id])
          : val?.[this.field.id];
      } else {
        if (
          this.isCanvasField &&
          this.context.field.assert.IsCanvas(this.field) &&
          this.currentCanvasBlockId
        ) {
          return findBlockById(
            this.currentCanvasBlockId,
            this.existingEntry?.[this.field.id]
          )?.value;
        } else return this.existingEntry?.[this.field.id];
      }
    }
  }
  get fieldAndAncestors() {
    return [...(this.parentFields || []), this.field];
  }

  get fieldIdPath() {
    return this.fieldAndAncestors
      .map(
        (f) =>
          // components in canvas fields will have a hashed id so we will
          // resolve the component id instead
          `${
            this.isCanvasField && f.dataFormat?.startsWith('component.')
              ? this.contensis.context.resolve.componentId(
                  f.dataFormat.replace('component.', '')
                )
              : f.id
          }${this.context.field.assert.IsRepeatable(f) ? '[]' : ''}`
      )
      .join('.');
  }

  get childFieldIdPath() {
    return this.fieldIdPath.split('.').slice(1).join('.') || undefined;
  }

  get parentField() {
    return this.parentFields?.[this.parentFields.length - 1];
  }

  constructor(
    public field: Field,
    private value: any,
    private entry: Entry,
    private context: IContensisContext,
    private contensis: ContensisRepository,
    public dependencies: EntryDependencyMeta[],
    private parentFields?: Field[],
    private isCanvasField = false
  ) {
    this.existingEntry = contensis.entries.findDependent(entry); // set this one time

    // Build the field, check dependencies and set the values for first pass and final
    this.init();

    if (this.isRequired) {
      if (
        (isUndefined(this.finalValue) ||
          (this.isRepeatable &&
            Array.isArray(this.finalValue) &&
            this.finalValue.length === 0)) &&
        // a partial payload merging into an existing entry is deliberately
        // incomplete at any depth, the API validates the merged entry on
        // commit - a new entry has nothing to merge so is still checked here
        !(this.context.payload?.partialEntries && this.existingEntry)
      ) {
        const message = `${this.context.field.assert.IsComposer(this.field) ? `composer ` : this.isRepeatable ? `repeatable ` : ''}${this.isComponent || this.isComponentChild ? `component ` : ''}field ${this.childFieldIdPath ? `${this.childFieldIdPath} ` : `${this.fieldIdPath} `}${this.isComposerChild ? `in composer ` : ''}has no ${this.isRepeatable ? 'items' : `value`}`;
        if (context.payload.ignoreErrors) {
          this.warnings = this.warnings.map(
            (w) => `${Logger.errorText(`Required`)} [${Logger.infoText(w)}]`
          );
          this.warnings.push(
            `${Logger.errorText(`Required`)} ${Logger.infoText(`[${message}]`)}`
          );
        } else
          throw new Error(`Required ${message}: ${this.warnings.join('; ')}`);
      } else {
        // dump warnings about required fields if the final value is set
        this.warnings = Logger.shouldLog('info')
          ? this.warnings.map((w) =>
              !w.startsWith('Incomplete') && !w.startsWith('did not')
                ? `Incomplete ${Logger.infoText(`[${w}]`)}`
                : w
            )
          : [];
      }
    } else {
      this.warnings = this.warnings.map((w) =>
        !w.startsWith('Incomplete') && !w.startsWith('did not')
          ? `Incomplete ${Logger.infoText(`[${this.context.field.assert.IsComposer(this.field) ? `composer ` : this.isRepeatable ? `repeatable ` : ''}${this.isComponent || this.isComponentChild ? `component ` : ''}field${this.childFieldIdPath ? ` ${this.childFieldIdPath}` : ''}${this.isComposerChild ? ` in composer` : ''}: ${w}]`)}`
          : w
      );
    }
  }

  /**
   * Return a final value for the field, iterating over any built fields if applicable
   * @returns entry as JSON
   */
  toJSON = (opts = { firstPass: false }) => {
    const { assert } = this.context.field;
    const { field } = this;

    // Iterate over builtFields and return finalValue
    if (assert.IsComponent(field)) {
      const componentJson = (fields: InnerFields | EntryField): any => {
        // Render each of the component fields to a JSON value
        const value = Array.isArray(fields)
          ? fields.map((bf) => componentJson(bf))
          : Object.entries(fields || {}).reduce((prev, [k, v]) => {
              if (Array.isArray(v)) {
                prev[k] = [];
                for (const inner of v) {
                  prev[k].push(inner.toJSON(opts));
                }
              } else prev[k] = v.toJSON(opts);
              return prev;
            }, {} as any);

        return Array.isArray(value) &&
          // Remove component objects if every key inside every item in a component array is blank
          value.every((item) =>
            Object.values(item).every((v) =>
              Array.isArray(v)
                ? v.every((inner) => isNullOrUndefined(inner))
                : isNullOrUndefined(v)
            )
          )
          ? []
          : value;
      };
      return componentJson(this.fields);
    } else if (assert.IsComposer(field)) {
      const composer = [] as { type: string; value: any }[];

      Object.entries(this.fields || []).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          const values = [];
          let type = '';
          for (const inner of v) {
            type = inner.field.id;
            values.push(inner.toJSON(opts));
          }
          if (values.length) composer.push({ type, value: values });
        } else {
          const value = v.toJSON(opts);
          if (!isNullOrUndefined(value))
            composer.push({ type: v.field.id, value });
        }
      });
      return composer;
    } else if (
      opts.firstPass === true &&
      (assert.IsEntry(field) || assert.IsCanvas(field)) &&
      this.requireSecondPass
    ) {
      return this.firstPassValue;
    } else {
      return this.finalValue;
    }
  };

  /**
   * Check the content type field and process the value from the source entry
   * setting the first pass and final values
   */
  init = () => {
    const { assert } = this.context.field;
    const { field, value } = this;

    // Entry-type field
    if (assert.IsEntry(field) || assert.IsAsset(field))
      this.finalValue = this.#buildEntryLink(value);
    // Image field
    else if (assert.IsImage(field)) this.finalValue = this.#buildImage(value);
    // Component field
    else if (assert.IsComponent(field))
      this.finalValue = this.#buildComponent(field.dataFormat, value);
    // Composer field
    else if (assert.IsComposer(field))
      this.finalValue = this.#buildComposer(field, value);
    // Canvas field
    else if (assert.IsCanvas(field)) {
      this.isCanvasField = true;
      this.finalValue = this.#buildCanvas(field, value);
    }
    // Resource picker field
    else if (assert.IsResourcePicker(field))
      this.finalValue = this.#buildResourcePicker(value);
    // Tag picker field
    else if (assert.IsTagPicker(field))
      this.finalValue = this.#buildTagPicker();
    // List field
    else if (assert.IsList(field))
      this.finalValue = this.#buildListField(value);
    // Taxonomy field
    else if (assert.IsTaxonomy(field))
      this.finalValue = this.#buildTaxonomyField(value);
    // Any other field we can reassign
    else this.finalValue = value;
  };

  #buildEntryLink = (value: Entry): any => {
    if (!value) return value;

    if (Array.isArray(value)) {
      const newEntryField = [];
      for (const item of value as Entry[]) {
        const newEntryLink = this.#mapEntrySys(item);
        if (newEntryLink) newEntryField.push(newEntryLink);
      }
      return value.length ? newEntryField : undefined;
    }

    return this.#mapEntrySys(value) || undefined;
  };

  #mapEntrySys = (link: Entry) => {
    const { context, contensis } = this;
    const sourceEntry = contensis.entries.resolveInSource(link.sys.id || '');
    const existingEntry = contensis.entries.findDependent(link);

    if (!existingEntry) {
      const entryLinkResolutionError = `link entry id ${contensis.logGuid(link)}`;
      // If the entry field is being built as part of a content model migration
      // don't mark as two-pass and check if we can stub the link value instead
      if (this.builtContentType) {
        // we can stub canvas blocks and omit links in other field types but not entry links in required fields
        this.#warning(`could not resolve ${entryLinkResolutionError}`);
        return; // return nothing so we can stub the entry link
      } else {
        // If the entry field has hit a dependency that does not exist
        // beyond the requested stop level then don't mark as two-pass
        // log a warning, and omit the link
        if (
          isNumber(context.payload.stopLevel) &&
          // However, if we can resolve the link to an entry in the source repository
          // we can assume it will be migrated in the same operation so we can mark for two-pass
          !sourceEntry
        ) {
          // Find which dependency level this entry is at
          const dependencyLevels =
            this.context.source.entries.dependencies.getEntryDependencyLevels(
              link?.sys?.id || ''
            );
          // If the entry is at a higher dependency level than the current stop level
          if (
            isArray(dependencyLevels) &&
            dependencyLevels[0] >= context.payload.stopLevel
          ) {
            const entryLinkStopLevelError = `stopped at level ${context.payload.stopLevel}`;
            this.#warning(
              `${entryLinkStopLevelError}, could not resolve ${entryLinkResolutionError}`
            );
            return; // return nothing so we can stub the entry link
          } else {
            // Use two-pass loading for unresolved entry links in regular entry migrations
            this.requireSecondPass = true;
            this.firstPassValue = undefined;
          }
        } else {
          // Use two-pass loading for unresolved entry links in regular entry migrations
          this.requireSecondPass = true;
          this.firstPassValue = undefined;
        }
      }
    }

    // check the entry link contentTypeId is allowed in the field
    // TODO: fix for canvas fields with all content types allowed
    const allowedContentTypeIds = context.field.GetAllowedContentTypes(
      this.field
    );
    const linkContentTypeId =
      existingEntry?.sys.contentTypeId ||
      sourceEntry?.sys.contentTypeId ||
      link.sys.contentTypeId ||
      'unknown';

    if (!allowedContentTypeIds.includes(linkContentTypeId)) {
      this.#warning(
        `linked entry content type '${linkContentTypeId}' not allowed`
      );
    }
    return this.#transformEntrySysId(link);
  };

  #transformEntrySysId = (value: any) => {
    if (!value) return value;

    let id: string | undefined;
    // try and find a target assetId to map from existingAssetIds
    if (
      (this.context.field.assert.IsAsset(this.field) ||
        this.context.field.assert.IsImage(this.field)) &&
      value?.sys?.id
    ) {
      id = this.contensis.existingAssetIds.find(
        ([sourceId]) => sourceId === value.sys.id
      )?.[1];
    }

    const managementFormatLinkField = {
      // entries sourced from delivery api have these where the management entry does not
      // this causes some entries to fail to load
      // ...(fieldValue || {}),
      sys: {
        ...(value?.sys || {}),
        id:
          typeof id !== 'undefined'
            ? id
            : this.contensis.transformGuid(value?.sys?.id),
        language: value?.sys.language || value?.language,
      },
    };

    // Resolve the target-side existing item so the strip helper can decide
    // which delivery artefacts (notably sys.language) are actually delivery-
    // only for this item vs. legitimately stored on management. existingValue
    // may return either a single link/asset shape ({sys}, {asset:{sys}}) or
    // an array of them (repeatable link fields, or ancestors that resolved
    // through a composer/repeatable) — for the array case, match by id so
    // we compare against the same item rather than the whole list.
    const existing = this.existingValue;
    const currentId = value?.sys?.id;
    let existingItem: any = existing;
    if (Array.isArray(existing)) {
      existingItem = currentId
        ? existing.find(
            (e: any) =>
              e?.sys?.id === currentId || e?.asset?.sys?.id === currentId
          )
        : undefined;
    }
    // Normalise onto .sys regardless of whether existing came wrapped in
    // .asset (image field shape) or was already a plain link ({sys:{...}}).
    const existingSys = existingItem?.asset?.sys ?? existingItem?.sys;

    this.#stripDeliverySysArtefacts(managementFormatLinkField.sys, existingSys);

    return managementFormatLinkField;
  };

  #buildComponent = (componentId?: string, value?: any) => {
    const {
      contensis,
      context: scopedContext,
      entry,
      isCanvasField,
      parentField,
    } = this;

    // The context needs to be fetched from the wider contensis scope
    // if the component is in a canvas field and the parent field has
    // the "kitchen sink" configuration
    const context =
      isCanvasField &&
      parentField &&
      contensis.context.field.assert.validation.IsAllowedTypesAll(parentField)
        ? contensis.context
        : scopedContext;

    const component = context.resolve.component(
      componentId,
      context.components
    );

    if (Array.isArray(value)) {
      this.fields = value.length ? [] : undefined; // Change builtFields to an array
      for (const item of value)
        if (!isNullOrUndefined(item)) {
          const newValue = {} as any;
          for (const field of component?.fields || []) {
            const ef = new EntryField(
              field,
              item[field.id],
              entry,
              context,
              contensis,
              this.dependencies.filter((d) =>
                d.fieldIdPath.startsWith(
                  `${this.fieldIdPath}.${field.id}${this.context.field.assert.IsRepeatable(field) ? '[]' : ''}`
                )
              ),
              this.fieldAndAncestors,
              this.isCanvasField
            );
            if (!isUndefined(ef.finalValue)) newValue[field.id] = ef;
            this.warnings.push(...ef.warnings);
            if (ef.requireSecondPass) this.requireSecondPass = true;
          }
          // Don't add empty components {} when builtFields is an array
          if (Object.keys(newValue).length) this.fields?.push(newValue);
        }
    } else {
      if (!isNullOrUndefined(value)) {
        this.fields = {}; // Change builtFields to an empty object
        for (const field of component?.fields || []) {
          const ef = new EntryField(
            field,
            value[field.id],
            entry,
            context,
            contensis,
            this.dependencies.filter((d) =>
              d.fieldIdPath.startsWith(`${this.fieldIdPath}.${field.id}`)
            ),
            this.fieldAndAncestors,
            this.isCanvasField
          );
          this.fields[field.id] = ef;
          this.warnings.push(...ef.warnings);
          if (ef.requireSecondPass) this.requireSecondPass = true;
        }
      }
    }
    return this.toJSON();
  };

  #buildComposer = (composerField: Field, composer: any[]) => {
    if (!isArray(composer)) return this.toJSON();

    const { contensis, context, entry } = this;
    const fields = context.field.GetComposerFields(composerField);

    this.fields = composer.length ? [] : undefined; // Change builtFields to an array
    for (const composerItem of composer) {
      const field = fields.find((f) => f.id === composerItem.type);
      const fieldValue = composerItem.value;

      if (field) {
        const ef = new EntryField(
          field,
          fieldValue,
          entry,
          context,
          contensis,
          this.dependencies.filter((d) =>
            d.fieldIdPath.startsWith(
              `${this.fieldIdPath}.${field.id}${this.context.field.assert.IsRepeatable(field) ? '[]' : ''}`
            )
          ),
          this.fieldAndAncestors,
          this.isCanvasField
        );
        this.fields?.push(ef);
        this.warnings.push(...ef.warnings);
        if (ef.requireSecondPass) this.requireSecondPass = true;
      }
    }
    return this.toJSON();
  };

  #buildTagPicker = () => {
    const { value } = this;
    if (!value) return value;

    if (Array.isArray(value)) {
      const newTagField = [];
      for (const tag of value as Tag[]) {
        const dependentTag = this.#findDependentTag(tag);

        newTagField.push(this.#buildTagObject(dependentTag));
      }
      return newTagField; // value.length ? newTagField : undefined;
    }

    const dependentTag = this.#findDependentTag(value);
    return this.#buildTagObject(dependentTag);
  };

  /** Finding a dependent tag is different to finding a dependent entry
   *  all tags will and must exist before an entry is created
   *  there is no two-pass for tags so we will fail the entire entry if
   *  we cannot find a dependent tag in the target repository or we cannot
   *  resolve it from an existing entry
   */
  #findDependentTag = (value: Tag) => {
    const { contensis, field } = this;
    if (isString(value)) {
      // An unresolved tag from a Delivery sourced entry would be just a string
      // and incompatible with the Management API
      const existingTag = contensis.tags.resolveByLabel(
        value,
        this.language,
        [this.existingValue].flat()
      );

      if (existingTag) return existingTag;
      throw new Error(
        `Tag '${value}' in field '${field.id}' requires an object with the tag id`
      );
    }

    // Look for a tag in the tags we've built for this migration
    const dependentTag = contensis.tags.migrateTags.find(
      (t) => t.originalId === value.id
    );
    if (!dependentTag) {
      const label = value.label[contensis.project.primaryLanguage];
      this.#warning(
        `did not resolve tag '${label}' in field '${field.id}' in target repository`
      );
      // return the original tag so we can try and create the entry
      // copy operations will not resolve target dependencies as they
      // are expected to already exist
      return value;
    }
    return dependentTag;
  };

  #buildTagObject = (tag: Tag | TagBuilder): ICreateTag => {
    if (tag instanceof TagBuilder) {
      const builtTag = tag.toJSON();

      return {
        groupId: builtTag.groupId,
        id: builtTag.id,
        label: builtTag.label,
        value: builtTag.value,
      };
    }
    return tag;
  };

  #buildResourcePicker = (value?: any) => {
    if (!value) return;
    const { contensis, field } = this;

    const build = (v: any) => {
      // Check the value is a valid contentTypeId
      const contentTypeId = v.id;
      const dataFormat = v.dataFormat;
      // Resolve content type from contensis context instead of this.context
      // as the resource picker field may allow "All"
      const resolved = contensis.context.resolve.contentType(contentTypeId);
      if (!resolved) {
        throw new Error(
          `${field.id} field, could not validate resource picker id: ${contentTypeId}, dataFormat: ${dataFormat}`
        );
      } else return v;
    };

    // Handle single value
    if (!Array.isArray(value)) {
      return build(value);
    }

    // Handle array value
    const finalValue = [];
    for (const inner of value) {
      finalValue.push(build(inner));
    }

    return finalValue;
  };

  // Delivery-only artefacts to remove from a sys object when producing a
  // management-format payload. Delivery api adds these, management does not.
  // When an `existingSys` is supplied (from a paired lookup in the target's
  // stored entry), values that exist there are preserved — this covers the
  // fact that some management-stored links carry sys.language and some don't.
  // When `existingSys` is passed we know what the target stores at this exact
  // path and can strip language/id only when the target genuinely lacks them.
  // When `existingSys` is undefined the caller couldn't pair a target sys
  // (e.g. traversal into a composer-nested canvas failed) — in that case we
  // preserve language/id and only strip the always-artefact keys (uri, node
  // display metadata) so we don't corrupt data on entries we can't compare.
  #stripDeliverySysArtefacts = (sys: any, existingSys?: any) => {
    if (!sys || typeof sys !== 'object') return;

    // sys.uri is a delivery artefact for entry/node/asset links (a rendered
    // url) but is the canonical value for uri-type links — only strip when
    // it's the artefact form.
    if (sys.linkProperties?.type !== 'uri') {
      if (this.contensis.context.isCopyOperation && !existingSys?.language)
        delete sys.language;
      delete sys.uri;
    }

    // node links carry the canonical id in sys.node.id; the outer sys.id/
    // language and node display metadata are delivery-only
    if (sys.node && typeof sys.node === 'object') {
      if (existingSys && !existingSys.id) delete sys.id;
      if (existingSys && !existingSys.language) delete sys.language;
      delete sys.node.displayName;
      delete sys.node.includeInMenu;
      delete sys.node.isCanonical;
      if (existingSys?.node && !existingSys.node.language)
        delete sys.node.language;
    }
  };

  // Recursively remove delivery-only artefacts from any value — a canvas
  // subtree, an entry link, an array of objects, etc. — so copy operations
  // produce a clean management-format payload without needing to walk the
  // full block-resolution loop below. Walks the source `value` and target's
  // `existing` in parallel so per-node decisions (see #stripDeliverySysArtefacts)
  // can consult the existing shape.
  #stripDeliveryArtefacts = (value: any, existing?: any): any => {
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        // pair canvas blocks by id when both source and existing items expose
        // one; fall back to array index for non-block arrays
        let existingItem: any;
        if (Array.isArray(existing)) {
          const sourceId = value[i]?.id;
          existingItem =
            sourceId != null
              ? (existing.find((e: any) => e?.id === sourceId) ?? existing[i])
              : existing[i];
        }
        this.#stripDeliveryArtefacts(value[i], existingItem);
      }
    } else if (value && typeof value === 'object') {
      this.#stripDeliverySysArtefacts(value.sys, existing?.sys);
      for (const key of Object.keys(value))
        if (key !== 'sys')
          this.#stripDeliveryArtefacts(value[key], existing?.[key]);
    }
    return value;
  };

  #buildCanvas = (canvasField: Field, value?: Block[], isNested = false) => {
    // In copy operations we do not need to transform or validate any links,
    // but we still need to strip delivery-format artefacts (sys.uri) that
    // would otherwise pollute the management-format payload
    if (this.contensis.context.isCopyOperation)
      return this.#stripDeliveryArtefacts(value, this.existingValue);

    if (!Array.isArray(value)) return;

    // Don't do anything fancy with canvas blocks as the content
    // in here cannot be broken down into individual EntryFields
    // We can still check dependencies and mark the entry for two-pass
    // if any dependency does not exist already
    const finalValue = [] as Block[];

    // Loop over canvas blocks
    for (const block of value) {
      if (!block) continue;
      this.currentCanvasBlockId = block.id;
      let newBlock: Block = { ...block };
      // Recurse if value is array
      if (Array.isArray(block.value)) {
        newBlock.value = this.#buildCanvas(canvasField, block.value, true);
      } else if (block.type === '_inlineEntry') {
        const newInlineEntry = this.#buildEntryLink(block.value as Entry);
        if (newInlineEntry) newBlock.value = newInlineEntry;
        else {
          const linkResolutionError = `inline entry in canvas field, id: ${(block.value as Entry)?.sys?.id} embedded in _inlineEntry`;
          // Transform the _inlineEntry block into a _fragment block if we cannot resolve
          if (this.contensis.context.payload.ignoreErrors) {
            (newBlock as Block).type = '_fragment';
            newBlock.value = `~[inline entry id: ${(block.value as Entry)?.sys?.id}]`;
            delete newBlock.properties;
            // Record the warning as Incomplete canvas content as we have made replacements and lost the link
            this.#warning(`stubbed ${linkResolutionError}`);
          } else throw new Error(`could not resolve ${linkResolutionError}`);
        }
      } else if (block.type === '_link') {
        if (block.properties?.link?.sys) {
          const sys = block.properties.link.sys;
          const linkBlock: LinkBlock = { ...block };
          //   - properties.link.sys.id / contentTypeId / language
          if (sys.dataFormat === 'entry' && linkBlock.properties) {
            const newEntryLink = this.#buildEntryLink({
              sys,
            });
            if (newEntryLink) linkBlock.properties.link = newEntryLink;
            else {
              const linkResolutionError = `entry link in canvas field, id: ${sys.id} embedded in _link: ${linkBlock.value}`;
              // Transform the _link block into a _fragment block if we cannot resolve
              // the source entry we are linking to and we're ignoring errors
              if (this.contensis.context.payload.ignoreErrors) {
                (linkBlock as Block).type = '_fragment';
                linkBlock.value = `${block.value}~[link entry id: ${sys.id}]`;
                delete linkBlock.properties;
                // Record the warning as Incomplete canvas content as we have made replacements and lost the link
                this.#warning(`stubbed ${linkResolutionError}`);
              } else
                throw new Error(`could not resolve ${linkResolutionError}`);
            }
          }
          if (sys.node?.id && linkBlock.properties?.link) {
            //   - properties.link.sys.node.id
            const newSys = this.#buildNodeLink(linkBlock.properties);
            if (newSys) linkBlock.properties.link.sys = newSys as any;
            else {
              const sourceNodeResolutionError = `source node in canvas field, id: ${sys.node?.id} embedded in _link: ${linkBlock.value}`;
              // Transform the _link block into a _fragment block if we cannot resolve
              // the source node we are linking to and we're ignoring errors
              if (this.contensis.context.payload.ignoreErrors) {
                (linkBlock as Block).type = '_fragment';
                linkBlock.value = `${block.value}~[link node id: ${sys.node?.id}]`;
                delete linkBlock.properties;
                // Record the warning as Incomplete canvas content as we have made replacements and lost the link
                this.#warning(`stubbed ${sourceNodeResolutionError}`);
              } else
                throw new Error(
                  `could not resolve ${sourceNodeResolutionError}`
                );
            }
          }
          newBlock = linkBlock;
        }
      } else if (block.type === '_image') {
        if (block.value) {
          // block.value.asset.sys.linkProperties.type === 'uri'
          // block.value.asset.sys.uri === 'https://www...'
          if ((block.value.asset?.sys as any).linkProperties?.type === 'uri')
            newBlock.value = block.value;
          else newBlock.value = this.#buildImage(block.value);
        }
      } else if (block.type === '_component') {
        // Component fields can be broken down into EntryField.fields
        // if we supply a stub component field configuration
        const ef = new EntryField(
          {
            dataType: 'object',
            dataFormat: `component.${block.properties?.component}`,
            id: block.id,
            name: { 'en-GB': block.id },
          },
          block.value,
          this.entry,
          this.context,
          this.contensis,
          this.dependencies.filter((d) =>
            d.fieldIdPath.startsWith(`${this.fieldIdPath}`)
          ),
          this.fieldAndAncestors,
          this.isCanvasField
        );
        this.warnings.push(...ef.warnings);
        if (ef.requireSecondPass) this.requireSecondPass = true;
        // Output the component JSON
        newBlock.value = ef.toJSON();
      } else if ((block.type as any) === '_formContentType') {
        const contentTypeId = (block.value as any).contentType.id;
        const resolved =
          this.contensis.context.resolve.contentType(contentTypeId);
        if (!resolved) {
          throw new Error(
            `${this.field.id} field, could not validate embedded form content type id: ${contentTypeId}`
          );
        }
      }
      finalValue.push(newBlock);
    }

    if (!isNested) this.finalValue = finalValue;
    return finalValue;
  };

  #buildNodeLink = (value: LinkBlock['properties']) => {
    if (!value?.link?.sys) return undefined;

    const { sys } = value.link;
    // Resolve the original source node
    const sourceNode = this.context.source.nodes.raw.find(
      (n) => n.id === sys.node?.id
    );
    // Locate existing node by id or path
    if (sourceNode) {
      const transformedId = this.contensis.transformGuid(
        sourceNode.id,
        sourceNode.path
      );
      const existingNode = this.contensis.nodes.raw.find(
        (n) => n.id === transformedId || n.path === sourceNode.path
      );
      if (existingNode) {
        return {
          node: { id: existingNode.id },
        };
      } else {
        // Node is not found by id or source path
        // mark entry as two-pass and create a new node link
        this.requireSecondPass = true;
        this.firstPassValue = [];
        return {
          node: { id: transformedId },
        };
      }
    } else {
      // We don't have a source node to build from (entries supplied in payload)
      // We have no idea what the path, the slug or the parent node would be
      // The best we can do in this case is either fire an error that
      // can be ignored with a flag, or transform the _link block into a _fragment
      // and update the value
      // `${value}-[unresolved link node id: ${value.link?.sys.node?.id}]`
      return undefined;
      // return { node: { id: value.link?.sys.node?.id } };
    }
  };

  #buildImage = (value: any) => {
    if (Array.isArray(value)) {
      return value
        .map((childImage) => this.#buildImageObject(childImage))
        .filter((v) => v);
    }
    return this.#buildImageObject(value);
  };

  #buildImageObject = (image: any) => {
    // check we have an image.asset, otherwise the mapping will fail
    if (
      !image ||
      typeof image !== 'object' ||
      !('asset' in image && image.asset)
    )
      return undefined;

    const assetId = image.asset.sys?.id;
    // The image is already attached to the field
    let resolvedAsset: EntryAsset | undefined;
    const existingValue = [this.existingValue]
      .flat()
      .find((v) => v?.asset?.sys?.id === assetId)?.asset;

    const { contensis } = this;
    if (!this.contensis.context.isCopyOperation) {
      resolvedAsset = contensis.entries.findDependent(
        image.asset
      ) as EntryAsset;
      if (!resolvedAsset) {
        resolvedAsset = contensis.entries.resolveInSource(
          assetId
        ) as EntryAsset;
        if (!resolvedAsset && !existingValue) {
          this.#warning(
            `could not resolve image id: ${contensis.logGuid(image.asset)} ${image?.asset?.entryTitle ? `entryTitle: ${image.asset.entryTitle} ` : ''}in ${this.fieldIdPath}`
          );
          return undefined;
        }
      }
      if (resolvedAsset) {
        this.#validateImageDimensions(resolvedAsset);
      } else if (existingValue) {
        this.#warning(
          `did not validate dimensions for image id: ${contensis.logGuid(image.asset)} ${image?.asset?.entryTitle ? `entryTitle: ${image.asset.entryTitle} ` : ''}in ${this.fieldIdPath}`
        );
      }
    }

    const mappedImage = {
      asset: {
        entryDescription: image.asset.entryDescription,
        entryTitle: image.asset.entryTitle,
        sys: {
          contentTypeId: image.asset.sys.contentTypeId,
          dataFormat: image.asset.sys.dataFormat,
          id: image.asset.sys.id,
          uri: '', // this needs to exist as an empty string so the diff function works
        },
        language: image.asset.sys.language,
      },
      altText: image.altText,
      caption: image.caption,
      transformations: this.#imageTransformations(image.transformations),
    };

    return this.#transformImageFieldGuids(mappedImage);
  };

  #validateImageDimensions = (image: any) => {
    // check the image dimensions are allowed in the field
    const allowedImageDimensions =
      this.contensis.context.field.assert.validation.ImageDimensions(
        this.field,
        image
      );
    if (typeof allowedImageDimensions === 'string') {
      this.#warning(allowedImageDimensions);
      return undefined;
    }

    return image;
  };

  #imageTransformations = (str: any) => {
    if (typeof str === 'object') return str;

    const params = new URLSearchParams(str);
    const transformations = Object.fromEntries(params.entries());

    if (transformations.w && transformations.h)
      return {
        size: {
          width: transformations.w,
          height: transformations.h,
        },
      };

    if (transformations.crop) {
      const cropParameters = transformations.crop.split(',');
      return {
        crop: {
          width: cropParameters[0],
          height: cropParameters[1],
          x: cropParameters[2],
          y: cropParameters[3],
        },
      };
    }

    return null;
  };

  #transformImageFieldGuids = (value: any) => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((v) => ({
        ...v,
        asset: { ...this.#transformEntrySysId(v.asset) },
      }));
    } else {
      return {
        ...value,
        asset: { ...this.#transformEntrySysId(value?.asset) },
      };
    }
  };

  #buildTaxonomyField = (taxonomyField: TaxonomyNode | TaxonomyNode[]) => {
    // Translate any delivery api format into management format
    if (
      Array.isArray(taxonomyField) &&
      !taxonomyField.every((v) => typeof v === 'string')
    ) {
      return taxonomyField.map((v) => {
        if (v && typeof v === 'object' && 'key' in v) return v.key;
        return v;
      });
    }
    if (
      taxonomyField &&
      typeof taxonomyField === 'object' &&
      'key' in taxonomyField
    )
      return taxonomyField.key;
    return taxonomyField as any[];
  };

  #buildListField = (value: any) => {
    // Validate the value(s) in the list field against the content type field allowed values
    const allowedValues = this.context.field.GetAllowedListValues(this.field);

    if (isArray(value)) {
      const finalValue = [];
      for (const v of value) {
        const listValue = this.#buildListValue(v, allowedValues);
        if (!isNullOrUndefined(listValue)) finalValue.push(listValue);
      }
      return finalValue;
    } else return this.#buildListValue(value, allowedValues);
  };

  #buildListValue = (value: string, allowedValues: string[]) => {
    // Validate the value against the content type field allowed values
    if (allowedValues.includes(value)) return value;
    this.#warning(`list value "${value}" not allowed`);
    return; // return nothing so we can omit the invalid list value
  };

  #warning = (message: string) => {
    const { context, warnings } = this;
    if (this.isRequired) {
      // if (context.payload.ignoreErrors) {
      warnings.push(`${message} in required field`);
      // } else throw new Error(`required ${message}`);
    } else {
      if (Logger.shouldLog('info'))
        warnings.push(
          `${!message.startsWith('did not') ? 'omitted ' : ''}${message}`
        );
    }
  };
}
